import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getClients, getProfiles, fullNameLastFirst } from '../lib/supabase';
import { loadApprovedCounties, serviceAreaStatus } from '../lib/serviceArea';
import { differenceInDays } from 'date-fns';
import { parseLocalDate } from '../lib/dateUtils';
import { formatDistanceToNow } from 'date-fns';
import DataTable from '../components/DataTable';
import AxisCareSyncModal from '../components/AxisCareSyncModal';
import { PAYERS, SERVICE_LINES, CLIENT_STATUSES, CLOSED_STATUSES, isDmeOnly, isReceivingService } from '../lib/serviceModel';

const GEORGIA_COUNTIES = ['Appling','Atkinson','Bacon','Baker','Baldwin','Banks','Barrow','Bartow','Ben Hill','Berrien','Bibb','Bleckley','Brantley','Brooks','Bryan','Bulloch','Burke','Butts','Calhoun','Camden','Candler','Carroll','Catoosa','Charlton','Chatham','Chattahoochee','Chattooga','Cherokee','Clarke','Clay','Clayton','Clinch','Cobb','Coffee','Colquitt','Columbia','Cook','Coweta','Crawford','Crisp','Dade','Dawson','Decatur','DeKalb','Dodge','Dooly','Dougherty','Douglas','Early','Echols','Effingham','Elbert','Emanuel','Evans','Fannin','Fayette','Floyd','Forsyth','Franklin','Fulton','Gilmer','Glascock','Glynn','Gordon','Grady','Greene','Gwinnett','Habersham','Hall','Hancock','Haralson','Harris','Hart','Heard','Henry','Houston','Irwin','Jackson','Jasper','Jeff Davis','Jefferson','Jenkins','Johnson','Jones','Lamar','Lanier','Laurens','Lee','Liberty','Lincoln','Long','Lowndes','Lumpkin','McDuffie','McIntosh','Macon','Madison','Marion','Meriwether','Miller','Mitchell','Monroe','Montgomery','Morgan','Murray','Muscogee','Newton','Oconee','Oglethorpe','Paulding','Peach','Pickens','Pierce','Pike','Polk','Pulaski','Putnam','Quitman','Rabun','Randolph','Richmond','Rockdale','Schley','Screven','Seminole','Spalding','Stephens','Stewart','Sumter','Talbot','Taliaferro','Tattnall','Taylor','Telfair','Terrell','Thomas','Tift','Toombs','Towns','Treutlen','Troup','Turner','Twiggs','Union','Upson','Walker','Walton','Ware','Warren','Washington','Wayne','Webster','Wheeler','White','Whitfield','Wilcox','Wilkes','Wilkinson','Worth'];

const STATUSES = CLIENT_STATUSES;
const pill = { display:'inline-block', fontSize:10, padding:'2px 7px', borderRadius:10, background:'var(--accent-bg)', color:'var(--accent)', fontWeight:500 };


const zoneBadge = (status) => {
  const map = {
    'New Referral':'badge-new','Client Contacted':'badge-new','Response Pending':'badge-pending','Waitlist':'badge-pending',
    'Intake Form Submitted':'badge-processing','Nursing Assessment Pending':'badge-processing','Nursing Assessment Completed':'badge-processing',
    'PPOT Request Pending':'badge-processing','PPOT Request In Progress':'badge-processing',
    'Submitted (DMA6A)':'badge-approval','Submitted (DMA80)':'badge-approval','Approved by GAPP':'badge-approval',
    'Active (Receiving Service)':'badge-active',
    'On-Hold/Suspended':'badge-closed','Service Suspended':'badge-closed','Discharged':'badge-closed','GAPP Application Denied':'badge-closed','Duplicate':'badge-closed','Lost':'badge-closed'
  };
  return map[status] || 'badge-new';
};

const eligibilityIcon = (status) => {
  if (status === 'Yes') return { color:'var(--success)', title:'Medicaid Eligible' };
  if (status === 'No') return { color:'var(--danger)', title:'Not Eligible' };
  return { color:'var(--warn)', title:'Eligibility Pending' };
};

const daysColor = (days, status) => {
  if (isReceivingService(status)) return 'var(--success)';
  if (CLOSED_STATUSES.includes(status)) return 'var(--text3)';
  if (days > 30) return 'var(--danger)';
  if (days > 14) return 'var(--warn)';
  return 'var(--success)';
};

const getPaExpiry = (client) => {
  if (!client.pa_expiry_date) return null;
  const days = differenceInDays(parseLocalDate(client.pa_expiry_date), new Date());
  return days;
};

const PaExpiryChip = ({ days }) => {
  if (days === null) return <span style={{ color: 'var(--text3)' }}>—</span>;
  const color = days <= 14 ? 'var(--danger)' : days <= 30 ? 'var(--warn)' : 'var(--success)';
  const bg = days <= 14 ? 'var(--danger-bg)' : days <= 30 ? 'var(--warn-bg)' : 'var(--success-bg)';
  const border = days <= 14 ? 'var(--danger-border)' : days <= 30 ? 'var(--warn-border)' : 'var(--success-border)';
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'2px 8px', borderRadius:12, fontSize:10, fontWeight:600, color, background:bg, border:`0.5px solid ${border}`, whiteSpace:'nowrap' }}>
      <span style={{ width:5, height:5, borderRadius:'50%', background:color, flexShrink:0 }} />
      {days < 0 ? `Expired` : `${days}d`}
    </span>
  );
};

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [staff, setStaff] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ zone:'', status:'', county:'', responsible:'', serviceType:'', clientType:'', eligibility:'', nurseAssessment:'', serviceArea:'' });
  const [sortBy, setSortBy] = useState('created_at');
  const [sortAsc, setSortAsc] = useState(false);
  const [showAxisSync, setShowAxisSync] = useState(false);
  const navigate = useNavigate();

  const loadClients = useCallback(async () => {
    setLoading(true);
    const activeFilters = {};
    if (filters.zone) activeFilters.zone = filters.zone;
    if (filters.status) activeFilters.status = filters.status;
    if (filters.county) activeFilters.county = filters.county;
    if (filters.responsible) activeFilters.responsible = filters.responsible;
    if (filters.serviceType) activeFilters.serviceType = filters.serviceType;
    if (filters.clientType) activeFilters.clientType = filters.clientType;
    if (filters.eligibility) activeFilters.eligibility = filters.eligibility;
    if (filters.nurseAssessment !== '') activeFilters.nurseAssessment = filters.nurseAssessment === 'true';
    activeFilters.sortBy = sortBy;
    activeFilters.sortAsc = sortAsc;
    const { data } = await getClients(activeFilters);
    setClients(data || []);
    setLoading(false);
  }, [filters, sortBy, sortAsc]);

  useEffect(() => { loadClients(); }, [loadClients]);
  useEffect(() => { getProfiles().then(({ data }) => setStaff(data || [])); }, []);
  // Service area is worked out here, from each client's county and Settings →
  // Approved Counties (lib/serviceArea.js) — there is no stored flag to query.
  const [approvedList, setApprovedList] = useState(null);
  useEffect(() => { loadApprovedCounties().then(({ data, error }) => setApprovedList(error ? null : data)); }, []);
  const shown = useMemo(() => (filters.serviceArea && approvedList)
    ? clients.filter(c => serviceAreaStatus(c.county, approvedList) === filters.serviceArea)
    : clients, [clients, filters.serviceArea, approvedList]);

  const setFilter = (key, val) => setFilters(prev => ({ ...prev, [key]: val }));
  const clearFilters = () => setFilters({ zone:'', status:'', county:'', responsible:'', serviceType:'', clientType:'', eligibility:'', nurseAssessment:'', serviceArea:'' });
  const activeFilterCount = Object.values(filters).filter(v => v !== '').length;

  // DataTable/TanStack sorting state — bridges to the existing sortBy/sortAsc
  // pair so loadClients() keeps doing server-side sorting unchanged.
  const sorting = useMemo(() => sortBy ? [{ id: sortBy, desc: !sortAsc }] : [], [sortBy, sortAsc]);
  const handleSortingChange = (updaterOrValue) => {
    const next = typeof updaterOrValue === 'function' ? updaterOrValue(sorting) : updaterOrValue;
    if (!next.length) return;
    setSortBy(next[0].id);
    setSortAsc(!next[0].desc);
  };

  const goToClient = (client) => navigate(`/clients/${client.id}`, { state: { clientIds: shown.map(c => c.id), idx: shown.indexOf(client) } });

  const columns = useMemo(() => [
    {
      accessorKey: 'client_id', header: 'ID', enableSorting: true, size: 80,
      meta: { mono: true, mobile: 'secondary' },
      cell: info => info.getValue(),
    },
    {
      id: 'name', accessorKey: 'last_name', header: 'Name', enableSorting: true, size: 210,
      meta: { mobile: 'primary' },
      cell: info => {
        const client = info.row.original;
        return (
          <>
            <span className="td-link" onClick={(e) => { e.stopPropagation(); goToClient(client); }}>
              {client.last_name}, {client.first_name}
            </span>
            {!client.nurse_assessment_complete && !isDmeOnly(client) && (
              <div style={{ fontSize:10, color:'var(--warn)', marginTop:2 }}>Assessment pending</div>
            )}
          </>
        );
      },
    },
    {
      id: 'service_type', header: 'Payer', size: 95,
      meta: { mobile: 'secondary' },
      cell: info => {
        const payer = PAYERS.find(p => p.key === info.row.original.service_type);
        return payer
          ? <span style={pill} title={payer.label}>{payer.short}</span>
          : <span style={{ color:'var(--text3)', fontSize:12 }}>—</span>;
      },
    },
    {
      // What we deliver — deliberately a separate column from Payer, so the
      // two axes of the service model are never read as one thing.
      id: 'service_lines', header: 'Service', size: 130,
      meta: { mobile: 'secondary' },
      cell: info => {
        const lines = info.row.original.service_lines || [];
        return (
          <span style={{ display:'flex', gap:4, flexWrap:'wrap' }}>
            {SERVICE_LINES.filter(l => lines.includes(l.key)).map(l => (
              <span key={l.key} style={{ ...pill, background:'var(--bg2)', color:'var(--text2)' }} title={l.desc}>
                {l.key === 'dme' ? 'DME' : l.label}
              </span>
            ))}
            {lines.length === 0 && <span style={{ color:'var(--text3)', fontSize:12 }}>—</span>}
          </span>
        );
      },
    },
    {
      id: 'client_type', header: 'Referral', size: 130,
      meta: { mobile: 'hidden' },
      cell: info => (Array.isArray(info.row.original.client_type) ? info.row.original.client_type : [info.row.original.client_type])
        .filter(t => t && t !== 'gapp' && t !== 'adult')
        .map(t => (
          <span key={t} style={{ display:'inline-block', fontSize:10, padding:'2px 6px', borderRadius:10, background:'var(--accent-bg)', color:'var(--accent)', marginRight:3, marginBottom:2 }}>{t}</span>
        )),
    },
    {
      accessorKey: 'county', header: 'County', enableSorting: true, size: 110,
      meta: { mobile: 'secondary' },
      cell: info => info.getValue() || '—',
    },
    {
      accessorKey: 'current_status', header: 'Status', enableSorting: true, size: 200,
      meta: { mobile: 'primary' },
      cell: info => <span className={`badge ${zoneBadge(info.getValue())}`}>{info.getValue()}</span>,
    },
    {
      id: 'eligibility', header: 'Eligibility', size: 110,
      meta: { mobile: 'secondary' },
      cell: info => {
        const client = info.row.original;
        const elig = eligibilityIcon(client.medicaid_eligible);
        return (
          <span title={elig.title}>
            <span style={{ color:elig.color, fontSize:18, lineHeight:1 }}>●</span>
            <span style={{ fontSize:11, color:'var(--text3)', marginLeft:4 }}>{client.medicaid_eligible || 'Pending'}</span>
          </span>
        );
      },
    },
    {
      accessorKey: 'pa_expiry_date', header: 'PA Expiry', enableSorting: true, size: 100,
      meta: { mobile: 'secondary' },
      cell: info => <PaExpiryChip days={getPaExpiry(info.row.original)} />,
    },
    {
      id: 'last_activity', header: 'Last Activity', size: 110,
      meta: { mobile: 'hidden' },
      cell: info => {
        const client = info.row.original;
        const lastActivity = client.last_activity_date
          ? formatDistanceToNow(new Date(client.last_activity_date), { addSuffix:true })
          : 'Never';
        const isStale = !client.last_activity_date || new Date(client.last_activity_date) < new Date(Date.now() - 7*24*60*60*1000);
        return <span style={{ fontSize:12, color: isStale ? 'var(--danger)' : 'var(--text2)' }}>{lastActivity}</span>;
      },
    },
    {
      id: 'last_comment', header: 'Last Comment', size: 200,
      meta: { mobile: 'hidden', truncate: true },
      cell: info => info.row.original.last_activity_note || '—',
    },
    {
      accessorKey: 'responsible_person_name', header: 'Responsible', enableSorting: true, size: 130,
      meta: { mobile: 'hidden' },
      cell: info => <span style={{ fontSize:12 }}>{info.getValue() || '—'}</span>,
    },
    {
      id: 'days_since_referral', accessorKey: 'referral_date', header: 'Days Since Referral', enableSorting: true, size: 90,
      meta: { mobile: 'hidden' },
      cell: info => {
        const client = info.row.original;
        const daysSince = client.referral_date
          ? Math.floor((new Date() - new Date(client.referral_date)) / (1000*60*60*24))
          : null;
        return <span style={{ color: daysColor(daysSince, client.current_status), fontWeight:500 }}>{daysSince !== null ? daysSince : '—'}</span>;
      },
    },
    {
      id: 'actions', header: '', size: 80,
      meta: { mobile: 'hidden' },
      cell: info => <button className="btn btn-ghost btn-sm" onClick={(e) => { e.stopPropagation(); goToClient(info.row.original); }}>View →</button>,
    },
  ], [clients]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap', alignItems:'center' }}>
        <button className={`btn btn-sm ${showFilters ? 'btn-primary' : 'btn-outline'}`} onClick={() => setShowFilters(!showFilters)}>
          ⚙ Filters {activeFilterCount > 0 && <span style={{ background:'#fff', color:'var(--navy)', borderRadius:10, padding:'0 6px', fontSize:11, marginLeft:4 }}>{activeFilterCount}</span>}
        </button>
        {activeFilterCount > 0 && <button className="btn btn-ghost btn-sm" onClick={clearFilters}>Clear all</button>}
        <div style={{ marginLeft:'auto', display:'flex', gap:8, flexWrap:'wrap' }}>
          {/* The "Check Eligibility" button was removed on 2026-10-19: it called
              check-eligibility, built for Availity, which was dropped, so it could
              never succeed. Eligibility is checked in GAMMIS (and, once enrolled,
              by the weekly 270 in the EDI transport). */}
          <button className="btn btn-outline btn-sm" onClick={() => setShowAxisSync(true)}>Sync AxisCare IDs</button>
          <button className="btn btn-outline btn-sm" onClick={() => navigate('/import')}>Import CSV</button>
          <button className="btn btn-outline btn-sm" onClick={() => navigate('/intake')}>+ New Intake</button>
          <button className="btn btn-primary btn-sm" onClick={() => navigate('/clients/new')}>+ Add Client</button>
        </div>
      </div>

      {showFilters && (
        <div className="card" style={{ marginBottom:16 }}>
          <div style={{ padding:16, display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(180px, 1fr))', gap:12 }}>
            <div className="form-group" style={{ margin:0 }}>
              <label className="form-label">Zone</label>
              <select className="form-select" value={filters.zone} onChange={e => setFilter('zone', e.target.value)}>
                <option value="">All Zones</option>
                {['Outreach','Clinical Intake','Authorization','Active','Inactive'].map(z => <option key={z}>{z}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ margin:0 }}>
              <label className="form-label">Status</label>
              <select className="form-select" value={filters.status} onChange={e => setFilter('status', e.target.value)}>
                <option value="">All Statuses</option>
                {STATUSES.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ margin:0 }}>
              <label className="form-label">County</label>
              <select className="form-select" value={filters.county} onChange={e => setFilter('county', e.target.value)}>
                <option value="">All Counties</option>
                {GEORGIA_COUNTIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ margin:0 }}>
              <label className="form-label">Service Area</label>
              <select className="form-select" value={filters.serviceArea} onChange={e => setFilter('serviceArea', e.target.value)}>
                <option value="">All</option>
                <option value="approved">Approved area</option>
                <option value="outside">Outside approved area</option>
                <option value="none">No county recorded</option>
                <option value="unrecognised">County not recognised</option>
              </select>
            </div>
            <div className="form-group" style={{ margin:0 }}>
              <label className="form-label">Responsible Person</label>
              <select className="form-select" value={filters.responsible} onChange={e => setFilter('responsible', e.target.value)}>
                <option value="">All Staff</option>
                {staff.map(s => <option key={s.id} value={s.full_name}>{fullNameLastFirst(s.full_name)}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ margin:0 }}>
              <label className="form-label">Service Type</label>
              <select className="form-select" value={filters.serviceType} onChange={e => setFilter('serviceType', e.target.value)}>
                <option value="">All</option>
                <option value="gapp">Georgia Pediatric Program (GAPP)</option>
                <option value="private">Private In-Home Care</option>
              </select>
            </div>
            <div className="form-group" style={{ margin:0 }}>
              <label className="form-label">Referral Type</label>
              <select className="form-select" value={filters.clientType} onChange={e => setFilter('clientType', e.target.value)}>
                <option value="">All</option>
                <option>New</option><option>Transfer</option><option>Spanish Speaking Only</option>
              </select>
            </div>
            <div className="form-group" style={{ margin:0 }}>
              <label className="form-label">Medicaid Eligibility</label>
              <select className="form-select" value={filters.eligibility} onChange={e => setFilter('eligibility', e.target.value)}>
                <option value="">All</option>
                <option value="Yes">Eligible</option>
                <option value="No">Not Eligible</option>
                <option value="Pending">Pending Check</option>
              </select>
            </div>
            <div className="form-group" style={{ margin:0 }}>
              <label className="form-label">Nurse Assessment</label>
              <select className="form-select" value={filters.nurseAssessment} onChange={e => setFilter('nurseAssessment', e.target.value)}>
                <option value="">All</option>
                <option value="true">Completed</option>
                <option value="false">Pending</option>
              </select>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <div className="card-title">
            Clients
            <span style={{ fontSize:12, color:'var(--text3)', fontWeight:400, marginLeft:8 }}>({shown.length})</span>
          </div>
        </div>

        <DataTable
          tableId="clients"
          columns={columns}
          data={shown}
          getRowId={c => c.id}
          onRowClick={goToClient}
          sorting={sorting}
          onSortingChange={handleSortingChange}
          manualSorting
          loading={loading}
          emptyMessage="No clients match your filters."
          maxHeight="calc(100vh - 260px)"
        />
      </div>

      {showAxisSync && (
        <AxisCareSyncModal onClose={() => setShowAxisSync(false)} onApplied={loadClients} />
      )}
    </div>
  );
}
